/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @angular-eslint/use-component-view-encapsulation */
import { Location } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SetupIntent, StripeCardCvcElement, StripeCardExpiryElement, StripeCardNumberElement, StripeElements, StripeError } from '@stripe/stripe-js';
import { UserMainComponent } from 'pages/users/user/main/user-main.component';
import { UserNotificationsComponent } from 'pages/users/user/notifications/user-notifications.component';
import { PaymentMethodDialogComponent } from 'pages/users/user/payment-methods/payment-method/payment-method.dialog.component';
import { UserSecurityComponent } from 'pages/users/user/security/user-security.component';
import { firstValueFrom } from 'rxjs';
import { AuthorizationService } from 'services/authorization.service';
import { CentralServerService } from 'services/central-server.service';
import { ComponentService } from 'services/component.service';
import { MessageService } from 'services/message.service';
import { SpinnerService } from 'services/spinner.service';
import { StripeService } from 'services/stripe.service';
import { WindowService } from 'services/window.service';
import { AbstractTabComponent } from 'shared/component/abstract-tab/abstract-tab.component';
import { ChargingStation, QrCodeData } from 'types/ChargingStation';
import { ActionResponse, BillingOperationResult } from 'types/DataResult';
import { RestResponse } from 'types/GlobalType';
import { OCPITokenType, OCPITokenWhitelist } from 'types/ocpi/OCPIToken';
import { QrTag, Tag } from 'types/Tag';
import { TenantComponents } from 'types/Tenant';
import { User, UserNotifications, UserRole } from 'types/User';
import { Utils } from 'utils/Utils';

import { SessionService } from '../service/session.service';

@Component({
  selector: 'app-payment',
  templateUrl: 'payment.component.html',
  styleUrls: ['payment.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
})
export class PaymentComponent extends AbstractTabComponent implements OnInit {
  @ViewChild('userNotificationsComponent') public userNotificationsComponent!: UserNotificationsComponent;
  @ViewChild('userSecurityComponent') public userSecurityComponent!: UserSecurityComponent;
  @ViewChild('userMainComponent') public userMainComponent!: UserMainComponent;
  @Input() public inDialog!: boolean;
  @Input() public dialogRef!: MatDialogRef<PaymentMethodDialogComponent>;
  @Input() public currentUserID!: string;
  @ViewChild('cardInfo', { static: true }) public cardInfo: ElementRef;
  public cardForm: FormGroup;
  public canListTags: boolean;
  panelOpenState = false;
  selectedValue: string;
  cardDetails: any = {};
  stripe: any;
  userRole: UserRole;
  userData: User;
  public isBillingComponentActive: boolean;
  public userID: string;
  public acceptConditions: AbstractControl;
  public elements: StripeElements;
  public cardNumber: StripeCardNumberElement;
  public expirationDate: StripeCardExpiryElement;
  public cvc: StripeCardCvcElement;
  public cardNumberError: string;
  public expirationDateError: string;
  public cvcError: string;
  public hasAcceptedConditions: boolean;
  public isCardNumberValid: boolean;
  public isExpirationDateValid: boolean;
  public isCvcValid: boolean;
  public isSaveClicked: boolean;
  public chargingStationId: string;
  public connectorId: number;
  public chargingStation: ChargingStation | null = null;
  codedChargingStationID: string;
  codedConnectorID: string;
  QrCodeData: QrCodeData | null = null;
  loggedUser: any;
  createdUserId: string;
  visualId: string;
  selectedLanguage = 'fr';
  tenantName = '';
  tenantLogo = '';
  private token: string;

  public constructor(
    activatedRoute: ActivatedRoute,
    windowService: WindowService,
    private router: Router,
    private location: Location,
    private fb: FormBuilder,
    private centralServerService: CentralServerService,
    private messageService: MessageService,
    private spinnerService: SpinnerService,
    private componentService: ComponentService,
    public translateService: TranslateService,
    private authorizationService: AuthorizationService,
    private route: ActivatedRoute,
    private stripeService: StripeService,
    private sessionService: SessionService
  ) {
    super(activatedRoute, windowService, ['all']);
    this.isBillingComponentActive = this.componentService.isActive(TenantComponents.BILLING);
    this.hasAcceptedConditions = false;
    this.isCardNumberValid = false;
    this.isExpirationDateValid = false;
    this.isCvcValid = false;
    this.isSaveClicked = false;
    this.createForm();
  }

  public async ngOnInit(): Promise<void> {
    this.spinnerService.hide();
    this.loggedUser = this.sessionService.getLoggedUser() || this.centralServerService.getLoggedUser();
    this.userID = this.sessionService.getUserId() || this.loggedUser?.id || '';
    console.log('PaymentComponent ~ ngOnInit ~ loggedUser:', this.loggedUser);
    this.route.paramMap.subscribe((params) => {
      this.codedChargingStationID = params.get('codedChargingStationID');
      this.codedConnectorID = params.get('codedConnectorID');
    });
    if (this.codedChargingStationID && this.codedConnectorID) {
      try {
        this.QrCodeData = await firstValueFrom(this.centralServerService.getQrCodeData(this.codedChargingStationID, this.codedConnectorID));
        if (this.QrCodeData) {
          this.chargingStationId = this.QrCodeData.chargingStationID;
          this.connectorId = Number(this.QrCodeData.connectorID);
          this.token = this.QrCodeData.token;
          console.log('PaymentComponent ~ ngOnInit ~ this.chargingStationId:', this.chargingStationId);
          console.log('PaymentComponent ~ ngOnInit ~ this.connectorId:', this.connectorId);
        } else {
          this.messageService.showErrorMessage('Failed to load QR code data');
        }
      } catch (error) {
        console.error('Error fetching QR code data:', error);
        this.messageService.showErrorMessage('Error loading payment data');
      }
    } else {
      console.error('codedChargingStationID or codedConnectorID is missing');
      this.messageService.showErrorMessage('Invalid charging station or connector ID');
    }

    this.loadTenantBrand();

    const language = this.translateService.currentLang || 'en';
    const supportedLanguages = ['en', 'fr', 'es', 'de', 'it', 'pt', 'cs', 'cz'];
    const languageToUse = supportedLanguages.includes(language) ? language : 'en';
    this.selectedLanguage = languageToUse;
    this.translateService.use(languageToUse);
    await this.initialize();
    this.selectedValue = '1';
  }

  createForm() {
    this.cardForm = this.fb.group({ acceptConditions: [false, Validators.requiredTrue] });
  }

  isCardFormValid(): boolean {
    return this.isCardNumberValid && this.isExpirationDateValid && this.isCvcValid;
  }

  redirectToEv24(): void {
    const params = new URLSearchParams({
      tenantName: this.tenantName || '',
      chargingStationName: this.chargingStationId || '',
      connectorId: this.connectorId?.toString() || '',
    });
    window.location.href = `https://qr.ev24.support/?${params.toString()}`;
  }

  public changeLanguage(language: string): void {
    this.selectedLanguage = language;
    this.translateService.use(language);
  }

  goBack(): void {
    this.location.back();
  }

  public createAllTrueNotifications = (): UserNotifications => ({
    sendSessionStarted: true,
    sendOptimalChargeReached: true,
    sendEndOfCharge: true,
    sendEndOfSession: true,
    sendUserAccountStatusChanged: true,
    sendNewRegisteredUser: true,
    sendUnknownUserBadged: true,
    sendChargingStationStatusError: true,
    sendChargingStationRegistered: true,
    sendOcpiPatchStatusError: true,
    sendOicpPatchStatusError: true,
    sendUserAccountInactivity: true,
    sendPreparingSessionNotStarted: true,
    sendOfflineChargingStations: true,
    sendBillingSynchronizationFailed: true,
    sendBillingPeriodicOperationFailed: true,
    sendSessionNotStarted: true,
    sendCarCatalogSynchronizationFailed: true,
    sendComputeAndApplyChargingProfilesFailed: true,
    sendEndUserErrorNotification: true,
    sendBillingNewInvoice: true,
    sendAdminAccountVerificationNotification: true,
  });

  public async onSubmit(): Promise<void> {
    console.log('PaymentComponent ~ onSubmit ~ this.cardForm.valid:', this.cardForm.valid);
    if (this.cardForm.valid && this.cardForm.controls['acceptConditions'].value && this.isCardFormValid()) {
      try {
        const userId = this.userID || this.loggedUser?.id;
        if (!userId) {
          throw new Error('User ID not available');
        }
        const setupIntent = await this.createSetupIntent(userId);
        if (!setupIntent) {
          this.messageService.showErrorMessage('Failed to create setup intent');
          return;
        }
        const confirmationResult = await this.confirmSetupIntent(setupIntent);
        if (confirmationResult.error) {
          this.messageService.showErrorMessage('Failed to confirm payment method');
          return;
        }
        const attachResult = await this.attachPaymentMethod(confirmationResult, userId);
        if (attachResult.error) {
          this.messageService.showErrorMessage('Failed to attach payment method');
          return;
        }
        const tagID = await this.assignTagToUser(this.loggedUser);
        this.sessionService.setTagId(tagID);
        // Store payment completion status
        this.sessionService.setPaymentStatus(userId, {
          isPaymentCompleted: true,
          paymentMethodId: typeof confirmationResult.setupIntent?.payment_method === 'string'
            ? confirmationResult.setupIntent?.payment_method
            : confirmationResult.setupIntent?.payment_method?.id,
          timestamp: new Date().toISOString()
        });
        // Navigate to charging page with state
        this.router.navigate(
          [`/qrcode/${this.codedChargingStationID}/${this.codedConnectorID}/charging`],
          {
            state: {
              qrCodeData: this.QrCodeData,
              paymentStatus: {
                isPaymentCompleted: true,
                userId,
                paymentMethodId: confirmationResult.setupIntent?.payment_method
              }
            }
          }
        );
      } catch (error) {
        console.error('PaymentComponent ~ onSubmit ~ Error:', error);
        this.messageService.showErrorMessage('An unexpected error occurred');
      }
    } else {
      this.messageService.showErrorMessage('Form is invalid or conditions are not accepted');
    }
  }

  public linkCardToAccount(userId: string) {
    this.isSaveClicked = true;
    void this.doCreatePaymentMethod(userId);
  }

  public handleAcceptConditions() {
    this.hasAcceptedConditions = !this.hasAcceptedConditions;
  }

  public close(saved: boolean = false) {
    if (this.inDialog) {
      this.dialogRef.close(saved);
    }
  }

  private loadTenantBrand(): void {
    this.tenantName = this.QrCodeData?.tenantName || '';
    const subdomain = this.windowService.getSubdomain();
    if (subdomain) {
      this.centralServerService.getTenantLogoBySubdomain(subdomain).subscribe({
        next: (tenantLogo: string) => {
          if (tenantLogo) {
            this.tenantLogo = tenantLogo;
          }
        },
        error: () => { /* Keep brand text fallback */ },
      });
    }
  }

  private async doCreatePaymentMethod(userId: string) {
    const operationResult: any = await this.createPaymentMethod(userId);
    if (operationResult.error) {
      if (operationResult.error.code === 'card_declined') {
        this.isCardNumberValid = false;
        this.messageService.showErrorMessage('settings.billing.payment_methods_create_error_card_declined');
        this.cardNumberError = this.translateService.instant('settings.billing.payment_methods_card_declined');
        this.cardNumber.focus();
      } else {
        this.messageService.showErrorMessage('settings.billing.payment_methods_create_error');
      }
      this.isSaveClicked = false;
    } else {
      this.messageService.showSuccessMessage('settings.billing.payment_methods_create_success', { last4: operationResult.internalData.card.last4 });
      this.close(true);
    }
  }

  private async createPaymentMethod(userId: string): Promise<any> {
    try {
      const setupIntent = await this.createSetupIntent(userId);
      const confirmationResult = await this.confirmSetupIntent(setupIntent);
      if (confirmationResult.error) {
        return confirmationResult;
      }
      return this.attachPaymentMethod(confirmationResult, userId);
    } catch (error) {
      Utils.handleHttpError(error, this.router, this.messageService, this.centralServerService, 'general.unexpected_error_backend');
    }
  }

  private async createSetupIntent(userId: string): Promise<any> {
    try {
      this.spinnerService.show();
      const response: BillingOperationResult = await this.centralServerService.setupPaymentMethod({ userID: userId }).toPromise();
      return response?.internalData;
    } finally {
      this.spinnerService.hide();
    }
  }

  private async confirmSetupIntent(setupIntent: SetupIntent): Promise<{ setupIntent?: SetupIntent; error?: StripeError }> {
    return this.getStripeFacade().confirmCardSetup(setupIntent.client_secret, { payment_method: { card: this.cardNumber } });
  }

  private async attachPaymentMethod(confirmationResult: { setupIntent?: SetupIntent; error?: StripeError }, userId: string): Promise<BillingOperationResult> {
    try {
      this.spinnerService.show();
      const response: BillingOperationResult = await this.centralServerService.setupPaymentMethod({
        setupIntentId: confirmationResult.setupIntent?.id,
        paymentMethodId: confirmationResult.setupIntent?.payment_method,
        userID: userId,
      }).toPromise();
      return response;
    } finally {
      this.spinnerService.hide();
    }
  }

  private async initialize(): Promise<void> {
    try {
      this.spinnerService.show();
      const stripeFacade = await this.stripeService.initializeStripe();
      if (!stripeFacade) {
        this.messageService.showErrorMessage('settings.billing.not_properly_set');
      } else {
        this.initializeCardElements();
      }
    } catch (error) {
      Utils.handleHttpError(error, this.router, this.messageService, this.centralServerService, 'general.unexpected_error_backend');
    } finally {
      this.spinnerService.hide();
    }
  }

  private getStripeFacade() {
    return this.stripeService.getStripeFacade();
  }

  private initializeCardElements() {
    this.elements = this.getStripeFacade().elements();
    const stripeStyle = {
      base: {
        color: '#173222',
        fontSize: '16px',
        fontFamily: 'Inter, Arial, sans-serif',
        fontSmoothing: 'antialiased',
        '::placeholder': {
          color: '#8a918b'
        }
      },
      invalid: {
        color: '#c62828'
      }
    };
    this.cardNumber = this.elements.create('cardNumber', { style: stripeStyle });
    this.cardNumber.mount('#cardNumber');
    this.cardNumber.on('change', event => {
      this.cardNumberError = event.error ? this.translateService.instant('settings.billing.payment_methods_card_number_error') : '';
      this.isCardNumberValid = !event.error && event.complete;
    });
    this.expirationDate = this.elements.create('cardExpiry', { style: stripeStyle });
    this.expirationDate.mount('#cardExp');
    this.expirationDate.on('change', event => {
      this.expirationDateError = event.error ? this.translateService.instant('settings.billing.payment_methods_expiration_date_error') : '';
      this.isExpirationDateValid = !event.error && event.complete;
    });
    this.cvc = this.elements.create('cardCvc', { style: stripeStyle });
    this.cvc.mount('#cardCvc');
    this.cvc.on('change', event => {
      this.cvcError = event.error ? this.translateService.instant('settings.billing.payment_methods_cvc_error') : '';
      this.isCvcValid = !event.error && event.complete;
    });
  }

  private async assignTagToUser(user: User): Promise<string> {
    return new Promise((resolve, reject) => {
      const tagID = Utils.generateTagID();
      const tag: Tag = { id: tagID, default: true, active: true, issuer: true, userID: user.id, visualID: tagID, description: 'Auto-generated tag for user', emspName: '' };
      this.centralServerService.createQrTag(tag).subscribe({
        next: (response: ActionResponse) => {
          if (response.status === RestResponse.SUCCESS) {
            console.log('Tag created successfully:', response.id);
            resolve(tagID);
          } else {
            this.messageService.showErrorMessage('Failed to create tag');
            reject(new Error('Failed to create tag'));
          }
        },
        error: (error) => {
          this.spinnerService.hide();
          Utils.handleHttpError(error, this.router, this.messageService, this.centralServerService, 'tags.create_error');
          reject(new Error('Failed to create tag'));
        }
      });
    });
  }
}
